/**
 * How canvas text responds to Stage zoom. Two classes:
 *
 * - Annotations (furniture names, wall / measure lengths, elevation
 *   dimensions) belong to the drawing, so they grow a little as you zoom in
 *   and shrink a little as you zoom out — enough to feel attached to the
 *   plan, never so much that they balloon or turn into specks.
 * - Chrome (collaborator name pills, drag hints, handle badges) is UI, so it
 *   stays the same size on screen at every zoom.
 *
 * Both are applied as a scale on the Group holding the text, inside the
 * zoomed Stage.
 */

/** How strongly annotations follow zoom: 0 = fixed on screen, 1 = scale with the drawing. */
export const LABEL_ZOOM_EXPONENT = 0.35;
/** Annotation size limits, as a share of the size at zoom 1. */
export const LABEL_MIN_SHARE = 0.85;
export const LABEL_MAX_SHARE = 1.35;

function safeZoom(zoom: number): number {
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

/** On-screen size of an annotation authored at `basePx` (its size at zoom 1). */
export function annotationScreenPx(basePx: number, zoom: number): number {
  const grow = Math.pow(safeZoom(zoom), LABEL_ZOOM_EXPONENT);
  return basePx * Math.min(LABEL_MAX_SHARE, Math.max(LABEL_MIN_SHARE, grow));
}

/** Group scale that turns stage-space annotation text into its on-screen size. */
export function annotationScale(zoom: number): number {
  return annotationScreenPx(1, zoom) / safeZoom(zoom);
}

/** Group scale that keeps chrome the same size on screen at every zoom. */
export function chromeScale(zoom: number): number {
  return 1 / safeZoom(zoom);
}

/**
 * Extra rotation (0 or 180°) for text inside a node rotated by `deg`, so it
 * never reads upside down. Vertical labels always read top-to-bottom.
 */
export function uprightFlip(deg: number): 0 | 180 {
  const n = ((deg % 360) + 360) % 360;
  return n > 90 && n <= 270 ? 180 : 0;
}

/** Rough advance of one Inter 600 character, as a share of the font size. */
const CHAR_ADVANCE = 0.6;
/** Characters that must fit before a label is worth drawing (ellipsis covers
 *  the rest) — below this a stub like "P…" is noise, not a name. */
export const MIN_LABEL_CHARS = 6;

/**
 * Whether a single-line label fits a box of `widthPx` × `heightPx` on
 * screen at `fontPx`: at least {@link MIN_LABEL_CHARS} characters (or the
 * whole text if shorter) plus padding, and room for one line.
 */
export function labelFits(
  text: string,
  fontPx: number,
  widthPx: number,
  heightPx: number,
  padPx = 5
): boolean {
  const chars = Math.min(Math.max(text.length, 1), MIN_LABEL_CHARS);
  return (
    widthPx - padPx * 2 >= chars * fontPx * CHAR_ADVANCE &&
    heightPx - padPx * 2 >= fontPx * 1.2
  );
}
